import React, {
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { GET_ALL_BUSINESSES } from '../../graphql/business/query';
import {
  AllBusinesses,
  AllBusinesses_allBusinesses_businesses,
  AllBusinessesVariables,
} from '../../graphql/business/types/AllBusinesses';
import { Operation, Resource } from '../../common/constants';
import { Column, Filter, Query } from 'material-table';
import MaterialTableComponent from '../../common/components/MaterialTableComponent';
import { materialTableLocaleData } from '../../locale/material-table';
import { IUserContext, UserContext } from '../../providers/UserProvider';
import { formatName } from '../../utils/common';
import MTableSelectFilterComponent from '../../common/components/MaterialTableComponents/MTableSelectFilterComponent';
import BusinessStatus from '../../constants/business';
import { Link } from '@material-ui/core';
import BusinessDetailsInfosModal from '../BusinessDetails/BusinessDetailsInfosModal';
import AddBoxIcon from '@material-ui/icons/AddBox';
import CustomDialog from '../../common/components/CustomDialog';
import CreateBusinessForms from './CreateBusinessForms';
import MTableFilterComponent from '../../common/components/MaterialTableComponents/MTableFilterComponent';
import EditIcon from '@material-ui/icons/Edit';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import {
  ConfigsContext,
  IConfigsContext,
} from '../../providers/ConfigsProvider';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useChangeBusinessStatus } from '../../hooks/mutations/useChangeBusinessStatus';
import { useStep } from '../../hooks/queries/useStep';
import { BusinessProvider } from '../BusinessDetails/BusinessProvider/BusinessProvider';
import {
  BusinessOrderByInput,
  BusinessWhereInput,
  SortOrder,
} from '../../types/graphql-global-types';
import { API_URL } from '../../config';

const Businesses = () => {
  const client = useApolloClient();

  const [businessDetailsId, setBusinessDetailsId] = useState<number | null>(
    null,
  );
  const { canI } = useContext<IUserContext>(UserContext);
  const { WEB_END_POINT } = useContext<IConfigsContext>(ConfigsContext);

  const [createDialogOpened, setCreateDialogOpened] = useState<boolean>(false);

  const [changeBusinessStatus] = useChangeBusinessStatus();

  const getBusinessCardUrl = useCallback(
    (userId: number | null | undefined) =>
      WEB_END_POINT && userId ? `${WEB_END_POINT}/contact/${userId}` : '',
    [WEB_END_POINT],
  );

  const canExportBusinesses = useMemo<boolean>(
    () => canI(Operation.EXPORT, Resource.BUSINESSES),
    [canI],
  );

  const canCreateBusiness = canI(Operation.CREATE, Resource.BUSINESSES);
  const canEditBusiness = canI(Operation.UPDATE, Resource.BUSINESSES);

  const { data: { CRMSteps: steps } = {} } = useStep();

  const stepLookup = useMemo<{ [p: string]: string }>(
    () =>
      (steps ?? []).reduce(
        (acc, { name, label }) => ({ ...acc, [name || '']: label }),
        {},
      ),
    [steps],
  );

  const tableColumns = useMemo(
    () =>
      [
        {
          title: 'idCC',
          editable: 'never',
          field: 'id',
          searchable: false,
          width: 80,
          filterComponent: ({ columnDef, onFilterChanged }: any) => (
            <MTableFilterComponent
              onChange={(value) =>
                !isNaN(+value) &&
                onFilterChanged(columnDef?.tableData?.id, value)
              }
            />
          ),
        },
        {
          title: 'idCRM',
          editable: 'never',
          field: 'idCRM',
          sorting: false,
          searchable: false,
          width: 80,
          filterComponent: ({ columnDef, onFilterChanged }: any) => (
            <MTableFilterComponent
              onChange={(value) =>
                !isNaN(+value) &&
                onFilterChanged(columnDef?.tableData?.id, value)
              }
            />
          ),
        },
        {
          title: 'Date',
          field: 'createdAt',
          editable: 'never',
          type: 'date',
          defaultGroupSort: 'desc',
          defaultSort: 'desc',
          searchable: false,
          width: 10,
        },
        {
          title: 'Nom',
          field: 'firstName',
          filtering: false,
          render: (business) =>
            `${business?.firstName ?? ''} ${business?.lastName ?? ''}`,
          editable: 'never',
        },
        {
          title: 'Téléphone',
          field: 'phone',
          emptyValue: '-',
          editable: 'never',
          filtering: false,
        },
        {
          title: 'Email',
          field: 'email',
          editable: 'never',
          filtering: false,
        },
        {
          title: "Apporteur d'affaire",
          field: 'apa.firstName',
          emptyValue: '-',
          editable: 'never',
          filterComponent: ({ columnDef, onFilterChanged }: any) => (
            <MTableFilterComponent
              onChange={(value) =>
                onFilterChanged(columnDef?.tableData?.id, value)
              }
            />
          ),
          render: (business) =>
            business?.apa?.isDeleted ? (
              <div>Utilisateur archivé</div>
            ) : (
              <Link
                href={getBusinessCardUrl(business?.apa?.id)}
                rel="noopener"
                target="_blank"
                color="inherit">
                {formatName({
                  firstName: business?.apa?.firstName,
                  lastName: business?.apa?.lastName,
                })}
              </Link>
            ),
        },
        {
          title: 'Agence',
          field: 'apa.agencyName',
          editable: 'never',
          filterComponent: ({ columnDef, onFilterChanged }: any) => (
            <MTableFilterComponent
              onChange={(value) =>
                onFilterChanged(columnDef?.tableData?.id, value)
              }
            />
          ),
          render: (business) => business?.apa?.agencyName,
        },
        {
          title: 'Expert Connectcrédit',
          field: 'mandatary.firstName',
          emptyValue: '-',
          editable: 'never',
          filterComponent: ({ columnDef, onFilterChanged }: any) => (
            <MTableFilterComponent
              onChange={(value) =>
                onFilterChanged(columnDef?.tableData?.id, value)
              }
            />
          ),
          render: (business) =>
            business?.mandatary?.isDeleted ? (
              <div>Utilisateur archivé</div>
            ) : (
              <Link
                href={getBusinessCardUrl(business?.mandatary?.id)}
                rel="noopener"
                target="_blank"
                color="inherit">
                {formatName({
                  firstName: business?.mandatary?.firstName,
                  lastName: business?.mandatary?.lastName,
                })}
              </Link>
            ),
        },
        {
          title: 'Statut',
          field: 'CRMStep.name',
          lookup: stepLookup,
          searchable: false,
          filterComponent: ({ columnDef, onFilterChanged }: any) => {
            return (
              <MTableSelectFilterComponent
                options={Object.keys(stepLookup).filter(
                  (_) => _ && _ !== BusinessStatus.REDIRECTED,
                )}
                labels={stepLookup}
                onChange={(value) =>
                  onFilterChanged(columnDef?.tableData?.id, value)
                }
              />
            );
          },
        },
      ] as Array<Column<AllBusinesses_allBusinesses_businesses>>,
    [getBusinessCardUrl, stepLookup],
  );

  const handleClose = () => {
    setBusinessDetailsId(null);
  };

  return (
    <>
      <MaterialTableComponent
        actions={[
          {
            icon: () => <GetAppIcon />,
            onClick: async (_, __) => {
              window.open(API_URL + '/api/v1/download/businesses');
            },
            isFreeAction: true,
            tooltip: 'Exporter sous Excel',
            disabled: !canExportBusinesses,
          },
          {
            icon: () => <AddBoxIcon />,
            onClick: (_, __) => {
              setCreateDialogOpened(true);
            },
            isFreeAction: true,
            tooltip: 'Créer une nouvelle affaire',
            disabled: !canCreateBusiness,
          },
          {
            icon: () => <EditIcon />,
            tooltip: 'Editer',
            onClick: (event, rowData) => {
              setBusinessDetailsId(rowData.id);
            },
            disabled: !canEditBusiness,
          },
        ]}
        icons={{
          Edit: forwardRef((props, ref) => (
            <ChevronRightIcon {...props} ref={ref} />
          )),
        }}
        title="Liste des affaires"
        columns={tableColumns}
        data={(query) =>
          new Promise(async (resolve) => {
            const { page, pageSize } = query;
            const search = query?.search?.trim() || undefined;

            const filters = query?.filters?.reduce<BusinessWhereInput>(
              (acc, filter) => ({
                ...acc,
                ...formatFilter(filter),
              }),
              {},
            );

            const { data } = await client.query<
              AllBusinesses,
              AllBusinessesVariables
            >({
              fetchPolicy: 'network-only',
              query: GET_ALL_BUSINESSES,
              variables: {
                input: {
                  take: pageSize,
                  skip: page * pageSize,
                  where: {
                    name: search,
                    phone: search,
                    email: search,
                    ...filters,
                  },
                  orderBy: formatOrderBy(query),
                },
              },
            });

            const totalCount = data?.allBusinesses?.totalCount ?? 0;
            const businesses = data?.allBusinesses?.businesses ?? [];

            resolve({
              totalCount,
              page,
              data: businesses,
            });
          })
        }
        isLoading={false}
        localization={{
          ...materialTableLocaleData.fr,
          header: {
            ...materialTableLocaleData.fr.header,
            actions: 'Action',
          },
        }}
        options={{
          filtering: true,
          grouping: false,
          search: true,
          pageSize: 10,
        }}
        editable={{
          isEditable: () => canEditBusiness,
          onRowUpdate: (newBusiness) => {
            return changeBusinessStatus({
              variables: {
                input: {
                  id: newBusiness?.id,
                  status: newBusiness?.CRMStep?.name,
                },
              },
            });
          },
        }}
      />
      <CustomDialog
        maxWidth="sm"
        open={createDialogOpened}
        title="Création affaire">
        <CreateBusinessForms
          onCancel={() => setCreateDialogOpened(false)}
          onCreate={() => setCreateDialogOpened(false)}
        />
      </CustomDialog>
      {businessDetailsId && (
        <BusinessProvider>
          <BusinessDetailsInfosModal
            handleClose={handleClose}
            businessId={businessDetailsId}
          />
        </BusinessProvider>
      )}
    </>
  );
};

const formatFilter = (filter: Filter<any>): BusinessWhereInput => {
  const key = filter?.column?.field as string;
  const value = filter?.value || undefined;

  const _parseNumber = (n: string | null | undefined): number | undefined =>
    n && !isNaN(+n) ? +n : undefined;

  const mapping: { [p: string]: any } = {
    id: { idCC: _parseNumber(value) },
    idCRM: { idCRM: _parseNumber(value) },
    'CRMStep.name': { step: value },
    'mandatary.firstName': { mandatary: { name: value } },
    'apa.firstName': { apa: { name: value } },
    'apa.agencyName': { apa: { agencyName: value } },
  };

  return mapping[key] || { [key]: value };
};

const formatOrderBy = (query: Query<any>): BusinessOrderByInput | undefined => {
  const key = query?.orderBy?.field;
  const direction = query?.orderDirection as SortOrder;

  if (!key || !direction) return undefined;

  const mapping: { [p: string]: any } = {
    id: { idCC: direction },
    firstName: { firstName: direction },
    'CRMStep.name': { step: direction },
    'mandatary.firstName': { mandatary: { firstName: direction } },
    'apa.firstName': { apa: { firstName: direction } },
  };

  return mapping[key as string] || { [key]: direction };
};

export default Businesses;
